import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send } from "lucide-react";
import { http, errText } from "@/lib/http";
import { useGuide } from "@/lib/guide";

const Rich = ({ text, onGo }) => text.split(/(\*\*[^*]+\*\*|Go to: \/[\w\-/]*)/g).map((p, i) => {
  if (p.startsWith("**") && p.endsWith("**")) return <strong key={i} className="text-foreground">{p.slice(2, -2)}</strong>;
  if (p.startsWith("Go to: /")) return <Link key={i} to={p.slice(7)} onClick={onGo} className="text-[#00E5FF] underline" data-testid="guide-go-link">Go to {p.slice(7)}</Link>;
  return p;
});

const STARTERS = ["How do I onboard a new client?", "How do I prove value at renewal?", "Why did a tool turn red?", "How do Jev gates work?"];

export const AskGuide = () => {
  const { pathname, setOpen } = useGuide();
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(sessionStorage.getItem("mh-guide-sid") || "");
  const end = useRef();
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs, busy]);
  const ask = async (text) => {
    if (!text.trim() || busy) return;
    setMsgs((m) => [...m, { role: "user", text }]); setQ(""); setBusy(true);
    try {
      const r = (await http.post("/guide/ask", { question: text, page: pathname, session_id: sid.current })).data;
      sid.current = r.session_id; sessionStorage.setItem("mh-guide-sid", r.session_id);
      setMsgs((m) => [...m, { role: "guide", text: r.answer }]);
    } catch (e) { setMsgs((m) => [...m, { role: "guide", text: errText(e, "The guide couldn't answer right now."), error: true }]); }
    setBusy(false);
  };
  return (
    <div className="flex flex-col h-full" data-testid="guide-ask">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {msgs.length === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Ask anything about using Manahouse. Answers come from the guide and use this workspace's AI allowance.</p>
            {STARTERS.map((s) => <button key={s} onClick={() => ask(s)} className="block w-full text-left text-sm rounded-lg border border-white/10 px-3 py-2 hover:border-[#D4FF3A]/50 transition-colors" data-testid={`guide-starter-${STARTERS.indexOf(s)}`}>{s}</button>)}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
            <div className={`max-w-[90%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#D4FF3A]/[0.12] border border-[#D4FF3A]/25" : m.error ? "border border-[#FF3B5C]/40 text-[#FF3B5C]" : "bg-white/[0.04] border border-white/[0.06]"}`} data-testid={`guide-msg-${i}`}><Rich text={m.text} onGo={() => setOpen(false)} /></div>
          </div>
        ))}
        {busy && <div className="font-mono text-[11px] text-muted-foreground">thinking…</div>}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(q); }} className="pt-3 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. how do I launch wave 1?" data-testid="guide-ask-input"
          className="flex-1 min-w-0 h-10 px-3.5 rounded-full bg-white/[0.03] border border-white/10 text-sm outline-none focus:border-[#D4FF3A]/60" />
        <button type="submit" disabled={busy || !q.trim()} data-testid="guide-ask-send" className="w-10 h-10 rounded-full bg-[#D4FF3A] text-black grid place-items-center disabled:opacity-40"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
};
